import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Switch,
  Textarea,
  DateField,
} from '@structural-climbing/ui'
import { CompetitionPoster, PosterImage, compressImage } from '@structural-climbing/ui'
import {
  COMPETITION_DISCIPLINES,
  COMPETITION_SCORING_TYPES,
  competitionBoards,
  type CompetitionDiscipline,
} from '@structural-climbing/validation'
import { useGymDisciplines } from '../../layouts/useGymDisciplines'
import type { Competition, CompetitionFormat, CompetitionScoringType } from './types'

interface Props {
  isOpen: boolean
  /** null = create; a competition = edit. One modal serves both. */
  editTarget: Competition | null
  onClose: () => void
  onSaved: () => void
}

const FORMATS: CompetitionFormat[] = ['long_running', 'single_day']

/** Matches the Worker's own limit, so a rejected upload is caught here first. */
const MAX_POSTER_BYTES = 5 * 1024 * 1024

/** What HeroUI hands `onSelectionChange`; the package does not export a type. */
type SelectKeys = 'all' | Iterable<unknown>

/**
 * Applies a HeroUI Select's new selection, ignoring an empty one.
 *
 * A single-selection Select fires `onSelectionChange` with an EMPTY set when
 * the already-selected item is clicked again. Spreading that gave `undefined`,
 * which dropped the key from the payload and had the Worker reject the save
 * with "discipline must be one of: boulder, lead" and no clue why.
 */
function pick<T extends string>(keys: SelectKeys, set: Dispatch<SetStateAction<T>>) {
  if (keys === 'all') return
  const next = [...keys][0]
  if (typeof next === 'string') set(next as T)
}

/** Trims a wall-clock value to the minute-precision value a DateField accepts. */
function toInputValue(value: string | undefined): string {
  return value ? value.slice(0, 16) : ''
}

export function CompetitionFormModal({ isOpen, editTarget, onClose, onSaved }: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t, i18n } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [prizes, setPrizes] = useState('')
  // A bouldering-only gym cannot run a lead or a combined competition. One that
  // already exists keeps its own discipline in the list, so it stays editable.
  const gymDisciplines = useGymDisciplines()
  const disciplineOptions = COMPETITION_DISCIPLINES.filter(
    (d) =>
      d === editTarget?.discipline ||
      competitionBoards(d).every((board) => gymDisciplines.includes(board)),
  )
  const [discipline, setDiscipline] = useState<CompetitionDiscipline>(gymDisciplines[0])
  const [format, setFormat] = useState<CompetitionFormat>('single_day')
  const [scoringType, setScoringType] = useState<CompetitionScoringType>('thousand_split')
  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [published, setPublished] = useState(false)
  const [joinPolicy, setJoinPolicy] = useState<'open' | 'members_only'>('members_only')
  // Kept as strings: an empty field means no fee, which a number cannot express.
  const [entryFee, setEntryFee] = useState('')
  const [entryFeeCurrency, setEntryFeeCurrency] = useState('RON')
  const [requiresConfirmation, setRequiresConfirmation] = useState(false)
  const [posterUrl, setPosterUrl] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /**
   * Validation messages keyed by payload field, from this form and from the
   * Worker alike. A rejected field has to say so on its own control: "Could
   * not save" at the bottom of a twelve-field modal tells nobody what to fix.
   */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!isOpen) return
    setName(editTarget?.name ?? '')
    setDescription(editTarget?.description ?? '')
    setPrizes(editTarget?.prizes ?? '')
    setDiscipline(editTarget?.discipline ?? gymDisciplines[0])
    setFormat(editTarget?.format ?? 'single_day')
    setScoringType(editTarget?.scoring_type ?? 'thousand_split')
    setStartsAt(toInputValue(editTarget?.starts_at))
    setEndsAt(toInputValue(editTarget?.ends_at))
    setPublished(editTarget?.published ?? false)
    setJoinPolicy(editTarget?.join_policy ?? 'members_only')
    setEntryFee(editTarget?.entry_fee_cents != null ? String(editTarget.entry_fee_cents / 100) : '')
    setEntryFeeCurrency(editTarget?.entry_fee_currency ?? 'RON')
    setRequiresConfirmation(editTarget?.requires_confirmation ?? false)
    setPosterUrl(editTarget?.poster_url ?? null)
    setError(null)
    setFieldErrors({})
  }, [isOpen, editTarget, gymDisciplines])

  async function handlePosterPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setUploading(true)
    setError(null)
    try {
      const compressed = await compressImage(file, MAX_POSTER_BYTES)
      const token = await getAccessTokenSilently()
      const body = new FormData()
      body.append('file', compressed)
      const res = await fetch(`${apiUrl}/competition-poster`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
      })
      if (!res.ok) throw new Error()
      const { url } = (await res.json()) as { url: string }
      // Held in state only: it reaches the competition when the form is saved.
      setPosterUrl(url)
    } catch {
      setError(t('competitions.errorPoster'))
    } finally {
      setUploading(false)
    }
  }

  /** Puts one message on one control and clears the generic banner. */
  function failField(field: string, message: string) {
    setFieldErrors({ [field]: message })
    setError(null)
  }

  async function handleSave() {
    if (!name.trim()) {
      failField('name', t('competitions.errorNameRequired'))
      return
    }
    if (!startsAt || !endsAt) {
      failField(startsAt ? 'ends_at' : 'starts_at', t('competitions.errorDatesRequired'))
      return
    }
    if (endsAt <= startsAt) {
      failField('ends_at', t('competitions.errorEndBeforeStart'))
      return
    }
    // The format is what the member page and the time-slot groups are built
    // around, so a "single day" spanning a week is not a harmless mismatch.
    if (format === 'single_day' && startsAt.slice(0, 10) !== endsAt.slice(0, 10)) {
      failField('ends_at', t('competitions.errorSingleDaySpansDays'))
      return
    }
    // A price with no currency is unreadable, so the pair moves together.
    if (entryFee && entryFeeCurrency.trim().length !== 3) {
      failField('entry_fee_currency', t('competitions.errorFeeNeedsCurrency'))
      return
    }

    setSaving(true)
    setError(null)
    setFieldErrors({})
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(
        editTarget ? `${apiUrl}/competitions/${editTarget.id}` : `${apiUrl}/competitions`,
        {
          method: editTarget ? 'PATCH' : 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: name.trim(),
            description: description.trim() || null,
            prizes: prizes.trim() || null,
            discipline,
            format,
            scoring_type: scoringType,
            starts_at: startsAt,
            ends_at: endsAt,
            published,
            join_policy: joinPolicy,
            // Sent in minor units, so 13 becomes 1300 and no float survives.
            entry_fee_cents: entryFee ? Math.round(Number(entryFee) * 100) : null,
            entry_fee_currency: entryFee ? entryFeeCurrency : null,
            requires_confirmation: requiresConfirmation,
            poster_url: posterUrl,
          }),
        },
      )
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          code?: string
          error?: string
          field?: string
          groups?: string[]
          rounds?: string[]
          routes?: string[]
        }
        // Moving the dates would strand a time-slot group or a round outside the
        // window. Name them, so it is clear what has to be fixed first.
        if (body.code === 'SESSIONS_OUTSIDE_WINDOW')
          setError(
            t('competitions.errorSessionsOutsideWindow', {
              groups: (body.groups ?? []).join(', '),
            }),
          )
        else if (body.code === 'ROUNDS_OUTSIDE_WINDOW')
          setError(
            t('competitions.errorRoundsOutsideWindow', {
              rounds: (body.rounds ?? []).join(', '),
            }),
          )
        // Narrowing the discipline would leave routes no board scores.
        else if (body.code === 'ROUTES_OTHER_DISCIPLINE')
          failField(
            'discipline',
            t('competitions.errorRoutesOtherDiscipline', {
              routes: (body.routes ?? []).join(', '),
            }),
          )
        // Every validation 400 from the Worker names the field that failed.
        // Prefer our own translated wording, and fall back to the Worker's
        // English so an unmapped field still says something specific.
        else if (body.field) {
          const key = `competitions.errorField_${body.field}`
          failField(body.field, i18n.exists(key) ? t(key) : (body.error ?? ''))
        } else setError(t('competitions.errorSaveFailed'))
        return
      }
      onSaved()
      onClose()
    } catch {
      setError(t('competitions.errorSaveFailed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader>
          {editTarget ? t('competitions.editTitle') : t('competitions.createTitle')}
        </ModalHeader>
        <ModalBody className="gap-4">
          <Input
            label={t('competitions.fieldName')}
            value={name}
            onValueChange={setName}
            isInvalid={!!fieldErrors.name}
            errorMessage={fieldErrors.name}
            isRequired
          />
          {/* Shown as a live preview, so a gym can see that leaving this alone
              still gives the competition a picture rather than a blank frame. */}
          <div>
            <p className="mb-1.5 text-sm text-foreground/70">{t('competitions.fieldPoster')}</p>
            <div className="flex items-center gap-3">
              <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg">
                {posterUrl ? (
                  // Whole, so the gym sees exactly what members will; a click
                  // opens it full screen.
                  <PosterImage
                    src={posterUrl}
                    zoomable="image"
                    labels={{
                      open: t('competitions.posterOpen'),
                      close: t('competitions.posterClose'),
                      zoomHint: t('competitions.posterZoomHint'),
                    }}
                  />
                ) : (
                  <CompetitionPoster
                    name={name || t('competitions.createTitle')}
                    showName={false}
                  />
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => void handlePosterPick(e)}
                />
                <Button
                  size="sm"
                  variant="flat"
                  isLoading={uploading}
                  onPress={() => fileInputRef.current?.click()}
                >
                  {posterUrl ? t('competitions.replacePoster') : t('competitions.uploadPoster')}
                </Button>
                {posterUrl && (
                  <Button
                    size="sm"
                    variant="light"
                    color="danger"
                    onPress={() => setPosterUrl(null)}
                  >
                    {t('competitions.removePoster')}
                  </Button>
                )}
              </div>
            </div>
            <p className="mt-1.5 text-xs text-foreground/50">{t('competitions.posterHint')}</p>
          </div>

          <Textarea
            label={t('competitions.fieldDescription')}
            description={t('competitions.fieldDescriptionHint')}
            value={description}
            onValueChange={setDescription}
            isInvalid={!!fieldErrors.description}
            errorMessage={fieldErrors.description}
            minRows={3}
          />
          <Textarea
            label={t('competitions.fieldPrizes')}
            description={t('competitions.fieldPrizesHint')}
            value={prizes}
            onValueChange={setPrizes}
            isInvalid={!!fieldErrors.prizes}
            errorMessage={fieldErrors.prizes}
            minRows={2}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label={t('competitions.fieldDiscipline')}
              selectedKeys={[discipline]}
              description={
                discipline === 'combined' ? t('competitions.disciplineHint_combined') : undefined
              }
              disallowEmptySelection
              isInvalid={!!fieldErrors.discipline}
              errorMessage={fieldErrors.discipline}
              onSelectionChange={(keys) => pick(keys, setDiscipline)}
            >
              {disciplineOptions.map((d) => (
                <SelectItem key={d} textValue={t(`competitions.discipline_${d}`)}>
                  {t(`competitions.discipline_${d}`)}
                </SelectItem>
              ))}
            </Select>
            <Select
              label={t('competitions.fieldFormat')}
              selectedKeys={[format]}
              disallowEmptySelection
              isInvalid={!!fieldErrors.format}
              errorMessage={fieldErrors.format}
              onSelectionChange={(keys) => pick(keys, setFormat)}
            >
              {FORMATS.map((f) => (
                <SelectItem key={f} textValue={t(`competitions.format_${f}`)}>
                  {t(`competitions.format_${f}`)}
                </SelectItem>
              ))}
            </Select>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <DateField
              withTime
              label={t('competitions.fieldStartsAt')}
              value={startsAt}
              onValueChange={setStartsAt}
              isInvalid={!!fieldErrors.starts_at}
              errorMessage={fieldErrors.starts_at}
              isRequired
            />
            <DateField
              withTime
              label={t('competitions.fieldEndsAt')}
              // A single-day competition cannot run past the day it starts, so
              // the picker will not offer another one.
              min={format === 'single_day' && startsAt ? startsAt : undefined}
              max={
                format === 'single_day' && startsAt ? `${startsAt.slice(0, 10)}T23:59` : undefined
              }
              value={endsAt}
              onValueChange={setEndsAt}
              isInvalid={!!fieldErrors.ends_at}
              errorMessage={fieldErrors.ends_at}
              isRequired
            />
          </div>

          {/* Editable after the competition starts on purpose: nothing is
              denormalised onto an ascent, so switching the formula just
              recomputes the board on the next read. */}
          <Select
            label={t('competitions.fieldScoring')}
            description={t(`competitions.scoringHint_${scoringType}`)}
            selectedKeys={[scoringType]}
            disallowEmptySelection
            isInvalid={!!fieldErrors.scoring_type}
            errorMessage={fieldErrors.scoring_type}
            onSelectionChange={(keys) => pick(keys, setScoringType)}
          >
            {COMPETITION_SCORING_TYPES.map((s) => (
              <SelectItem key={s} textValue={t(`competitions.scoring_${s}`)}>
                {t(`competitions.scoring_${s}`)}
              </SelectItem>
            ))}
          </Select>

          <Select
            label={t('competitions.fieldJoinPolicy')}
            description={t('competitions.joinPolicyHint')}
            selectedKeys={[joinPolicy]}
            disallowEmptySelection
            isInvalid={!!fieldErrors.join_policy}
            errorMessage={fieldErrors.join_policy}
            onSelectionChange={(keys) => pick(keys, setJoinPolicy)}
          >
            {(['open', 'members_only'] as const).map((p) => (
              <SelectItem key={p} textValue={t(`competitions.joinPolicy_${p}`)}>
                {t(`competitions.joinPolicy_${p}`)}
              </SelectItem>
            ))}
          </Select>

          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              type="number"
              min={0}
              step="0.01"
              label={t('competitions.fieldEntryFee')}
              description={t('competitions.entryFeeHint')}
              value={entryFee}
              onValueChange={setEntryFee}
              isInvalid={!!fieldErrors.entry_fee_cents}
              errorMessage={fieldErrors.entry_fee_cents}
            />
            <Input
              label={t('competitions.fieldEntryFeeCurrency')}
              value={entryFeeCurrency}
              onValueChange={(v) => setEntryFeeCurrency(v.toUpperCase().slice(0, 3))}
              isInvalid={!!fieldErrors.entry_fee_currency}
              errorMessage={fieldErrors.entry_fee_currency}
              isDisabled={!entryFee}
            />
          </div>

          <Switch isSelected={requiresConfirmation} onValueChange={setRequiresConfirmation}>
            <span className="text-sm">
              {t('competitions.fieldRequiresConfirmation')}
              <span className="block text-xs text-foreground/50">
                {t('competitions.requiresConfirmationHint')}
              </span>
            </span>
          </Switch>

          <Switch isSelected={published} onValueChange={setPublished}>
            <span className="text-sm">
              {t('competitions.fieldPublished')}
              <span className="block text-xs text-foreground/50">
                {t('competitions.fieldPublishedHint')}
              </span>
            </span>
          </Switch>

          {error && <p className="text-sm text-danger">{error}</p>}
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose} isDisabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button color="primary" onPress={() => void handleSave()} isLoading={saving}>
            {t('common.save')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
